import { Track } from "@/src/lib/tracking";
/**
 * TokenPopover数据处理工具函数
 */

import { dayjs } from "utils";
import { getStockPriceData } from "./getStockPriceData";
import type {
  PriceChangeStatus,
  SocialLink,
  ChartData,
  TokenApiResponse,
  TokenPopoverData,
  FormatOptions,
  AssetType,
  ChatCategoryCardItem,
  CardItem,
} from "./types";
import {
  DEFAULT_VALUES,
  DECIMAL_PRECISION,
  TIME_CONFIG,
  LOG_PREFIX,
} from "./constants";
import { formatPriceForDisplay } from "@components/PriceDisplay/utils";
import Translations from "@translate/hooks/useTokenPopoverData/utils";
import { I18nUtil } from "@utils/i18n";
import { Stock } from "@features/api/openData";

const I18n = I18nUtil.createScoped(Translations);

/**
 * 格式化百分比显示
 * @param percentage 百分比值
 * @param options 格式化选项
 * @returns 格式化后的百分比字符串
 */
export const formatPercentage = (
  percentage: number | string | null | undefined,
  options: FormatOptions = {},
): string => {
  const { fallbackValue = DEFAULT_VALUES.PERCENTAGE_FALLBACK } = options;

  // 检查输入值有效性
  if (
    percentage === null ||
    percentage === undefined ||
    percentage === "" ||
    isNaN(Number(percentage))
  ) {
    return fallbackValue;
  }

  const numPercentage = Number(percentage);

  // 零值处理
  if (numPercentage === 0) {
    return fallbackValue;
  }

  return `${numPercentage.toFixed(DECIMAL_PRECISION.PERCENTAGE)}%`;
};

/**
 * 计算价格变化状态
 * @param percentage 百分比变化值
 * @returns 价格变化状态
 */
export const calculateChangeStatus = (
  percentage: number | string | null | undefined,
): PriceChangeStatus => {
  if (
    percentage === null ||
    percentage === undefined ||
    percentage === "" ||
    isNaN(Number(percentage))
  ) {
    return "flat";
  }

  const numPercentage = Number(percentage);

  if (numPercentage > 0) {
    return "up";
  }
  if (numPercentage < 0) {
    return "down";
  }
  return "flat";
};

/**
 * 生成时间范围（当前时间-7天 到 当前时间）
 * @returns 时间范围对象
 */
export const generateTimeRange = () => {
  const endTime = dayjs().valueOf();
  const startTime = dayjs()
    .subtract(TIME_CONFIG.CHART_DAYS_RANGE, "day")
    .valueOf();

  return { startTime, endTime };
};

/**
 * 格式化社交媒体数据
 * @param socialMedias 原始社交媒体数据
 * @returns 格式化后的社交媒体链接数组
 */
const formatSocialMedias = (
  socialMedias: Record<string, string> | undefined,
): SocialLink[] => {
  if (!socialMedias || typeof socialMedias !== "object") {
    return [];
  }

  return Object.entries(socialMedias)
    .map(([platform, url]) => {
      const platformKey = platform.toLowerCase();

      return {
        key: platformKey,
        url,
      };
    })
    .filter((social) => social.url);
};

/**
 * 格式化图表数据
 * @param ohlcData OHLC原始数据
 * @returns 格式化后的图表数据
 */
const formatChartData = (
  ohlcData:
    | Array<{
        time: number;
        open: string;
        high: string;
        low: string;
        close: string;
        volume?: string;
      }>
    | undefined,
): ChartData => {
  if (!ohlcData || !Array.isArray(ohlcData) || ohlcData.length === 0) {
    return {
      data: [...DEFAULT_VALUES.EMPTY_CHART_DATA],
      timestamps: [],
      volume: [],
    };
  }

  return {
    data: ohlcData.map((item) => Number(item.close) || 0),
    timestamps: ohlcData.map((item) => item.time),
    volume: ohlcData.map((item) => Number(item.volume) || 0),
  };
};

/**
 * 验证Token数据有效性
 * @param data Token API响应数据
 * @returns 是否有效
 */
export const validateTokenData = (data: any): data is TokenApiResponse => {
  return !!(
    data &&
    typeof data.name === "string" &&
    // 兼容symbol和base_symbol字段
    (typeof data.symbol === "string" || typeof data.base_symbol === "string") &&
    data.market_data &&
    data.market_data.current_price !== null &&
    data.market_data.current_price !== undefined
  );
};

/**
 * 验证Stock数据有效性
 * @param data Stock API响应数据
 * @returns 是否有效
 */
export const validateStockData = (data: any) => {
  return !!(
    data &&
    typeof data.company_name === "string" &&
    typeof data.name === "string"
  );
};

/**
 * 将Token API响应转换为TokenPopover数据格式
 * @param apiData Token API响应数据
 * @param assetType 资产类型
 * @returns 格式化后的TokenPopover数据
 */
export const transformTokenApiData = (
  apiData: TokenApiResponse,
  assetType: AssetType,
  tokenId?: string,
  tokenType?: AssetType,
): TokenPopoverData => {
  // 处理价格数据
  const currentPrice = apiData.market_data.current_price;

  // 获取24小时价格变化百分比 - 新API数组格式
  const change24h = apiData.market_data.price_change_percentage?.find(
    (item) => item.time_period === "24h",
  );
  const priceChangePercentage = change24h
    ? Number(change24h.percentage)
    : undefined;

  return {
    token: {
      id: tokenId,
      type: tokenType,
      name: apiData.name,
      symbol: apiData.base_symbol || apiData.symbol || "", // 优先使用base_symbol
      avatarSrc: apiData.logo,
      address:
        assetType === "pool"
          ? apiData.address || apiData.base_address
          : apiData.base_address || apiData.address,
      socials: formatSocialMedias(apiData.social_media),
    },
    price: {
      current: {
        priceData: formatPriceForDisplay(currentPrice),
        type: "crypto",
        marketChangeLabel: I18n.t("last 24hrs"),
      },
      change: {
        percentage: formatPercentage(priceChangePercentage),
        status: calculateChangeStatus(priceChangePercentage),
      },
    },
    chart: formatChartData(
      apiData.ohlc?.map((item) => ({
        time: Number(item.time || item.timestamp), // 兼容time和timestamp字段
        open: String(item.open),
        high: String(item.high),
        low: String(item.low),
        close: String(item.close),
        volume: item.volume ? String(item.volume) : undefined,
      })),
    ),
    score: apiData.latest_rating?.level?.final
      ? {
          score: apiData.latest_rating.level.final,
        }
      : undefined,
  };
};

/**
 * 将Stock API响应转换为TokenPopover数据格式
 * @param apiData Stock API响应数据
 * @returns 格式化后的TokenPopover数据
 */
export const transformStockApiData = (
  apiData: Stock,
  tokenType?: AssetType,
): TokenPopoverData => {
  // 使用 getStockPriceData 处理股票价格数据
  const stockPriceData = getStockPriceData({
    marketData: apiData.trading_data,
    currentPrice: apiData.trading_data?.current_price?.toString(),
  });
  // 获取第一个市场数据项，如果为空则提供默认值
  const firstMarketData = stockPriceData.marketDataList[
    stockPriceData.marketDataList.length - 1
  ] || {
    current_price: apiData.trading_data?.current_price || 0,
    marketLabel: "",
    price_change_percentage: 0,
  };

  return {
    token: {
      id: undefined,
      type: tokenType,
      name: apiData.company_name || "",
      symbol: apiData.name || "",
      avatarSrc: apiData.logo || "",
      socials: [], // Stock类型通常没有社交媒体
    },
    price: {
      current: {
        currency: apiData.currency,
        type: "stock",
        current_price: firstMarketData?.current_price?.toString(),
        marketLabel: firstMarketData?.marketLabel?.split(":")?.[0],
        price_change_percentage:
          firstMarketData?.price_change_percentage?.toString(),
      },
      change: {
        percentage: formatPercentage(firstMarketData?.price_change_percentage),
        status: calculateChangeStatus(firstMarketData?.price_change_percentage),
      },
    },
    chart: formatChartData(
      apiData.ohlcv?.map((item) => ({
        time: Number(item.time), // 兼容time和timestamp字段
        open: String(item.open),
        high: String(item.high),
        low: String(item.low),
        close: String(item.close),
        volume: item.volume ? String(item.volume) : undefined,
      })),
    ),
    score: apiData.latest_rating?.level,
  };
};

/**
 * 错误处理工具函数
 * @param error 错误对象
 * @param assetType 资产类型
 * @param context 错误上下文
 */
export const handleError = (
  error: any,
  assetType: AssetType | string,
  context: string = "data processing",
): void => {
  // tag 用固定前缀，可变部分（assetType / context）进参数 —— msg 保持常量才可聚合
  Track.error(error, {
    tag: LOG_PREFIX,
    msg: "data processing error",
    asset_type: String(assetType),
    context,
  });
};

/**
 * 调试日志工具函数
 * @param message 日志消息
 * @param data 可选的数据对象
 */
/**
 * 类型守卫函数：检查字符串是否为有效的AssetType
 * @param type 待检查的字符串
 * @returns 是否为有效的AssetType
 */
export const isAssetType = (type: string): type is AssetType => {
  return ["token", "stock", "pool"].includes(type);
};

/**
 * 将ChatCategoryCard的cardItem转换为hook所需的CardItem格式
 * @param chatCardItem ChatCategoryCard的cardItem
 * @returns 转换后的CardItem或null
 */
export const transformChatCardItemToCardItem = (
  chatCardItem: ChatCategoryCardItem | null | undefined,
): CardItem | null => {
  if (!chatCardItem || !chatCardItem.type) {
    return null;
  }

  // 类型转换和验证
  const assetType = chatCardItem.type;
  if (!assetType || !isAssetType(assetType)) {
    return null;
  }

  const { properties, ...otherProps } = chatCardItem;

  return {
    ...otherProps,
    type: assetType,
    properties: {
      id: properties?.id,
      symbol: properties?.symbol,
      ...(properties || {}),
    },
  };
};
